<template>
  <section class="project-tax-information">
    <div class="d-flex flex-wrap justify-content-between align-items-start mb-1 gap-1">
      <div>
        <h4 class="mb-25">Facturación del proyecto</h4>
        <p class="text-muted mb-0">
          Selecciona la razón social por default. Las nuevas cotizaciones del proyecto se ligarán a esta razón social y luego podrán cambiarse desde la cotización si hace falta.
        </p>
      </div>
      <div class="d-flex flex-wrap gap-1">
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="loading || !resolvedCustomerId"
          @click="loadTaxInformations"
        >
          Actualizar razones sociales
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :to="newBillingRoute"
          :disabled="!resolvedCustomerId"
        >
          Registrar razón social
        </b-button>
      </div>
    </div>

    <b-alert
      v-if="!resolvedCustomerId"
      show
      variant="warning"
      class="mb-1"
    >
      Guarda primero el cliente del proyecto para poder elegir una razón social.
    </b-alert>

    <b-alert
      v-else-if="!loading && !hasOptions"
      show
      variant="warning"
      class="mb-1"
    >
      El cliente aún no tiene razones sociales registradas.
    </b-alert>

    <b-form-group
      v-else
      label="Razón social por default"
      label-for="project-tax-information"
      class="mb-1"
    >
      <b-form-select
        id="project-tax-information"
        :value="value"
        :disabled="loading"
        :options="taxInformationOptions"
        @input="$emit('input', $event)"
      />
      <small class="text-muted d-block mt-50">
        Si la cotización usa otra razón social, se podrá cambiar desde la propia cotización sin afectar el default del proyecto.
      </small>
    </b-form-group>

    <b-card
      v-if="selectedTaxInformation"
      no-body
      class="project-tax-information__summary"
    >
      <b-card-body>
        <small class="text-uppercase text-primary font-weight-bold d-block mb-50">Razón social seleccionada</small>
        <h5 class="mb-50">{{ selectedTaxInformation.corporate_name || 'Razón social sin nombre' }}</h5>
        <p class="mb-25">RFC: {{ selectedTaxInformation.rfc || '—' }}</p>
        <p class="mb-25">Régimen fiscal: {{ selectedTaxInformation.fiscal_regime || '—' }}</p>
        <p class="mb-25">Uso de CFDI: {{ selectedTaxInformation.cfdi_use || '—' }}</p>
        <p class="mb-0 text-muted">{{ summaryAddress }}</p>
      </b-card-body>
    </b-card>
  </section>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'ProjectTaxInformationSelector',
  props: {
    customerId: {
      type: String,
      default: null,
    },
    value: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      taxInformations: [],
    }
  },
  computed: {
    resolvedCustomerId() {
      return this.customerId || null
    },
    hasOptions() {
      return this.taxInformations.length > 0
    },
    taxInformationOptions() {
      const baseOption = {
        value: null,
        text: this.loading ? 'Cargando razones sociales...' : 'Sin razón social por default',
      }

      return [
        baseOption,
        ...this.taxInformations.map(item => ({
          value: item.id,
          text: `${item.corporate_name || 'Razón social'}${item.rfc ? ` (${item.rfc})` : ''}`,
        })),
      ]
    },
    selectedTaxInformation() {
      return this.taxInformations.find(item => item.id === this.value) || null
    },
    summaryAddress() {
      const info = this.selectedTaxInformation || {}
      return [info.street, info.ext_number, info.suburb, info.city, info.state, info.postal_code]
        .filter(value => typeof value === 'string' && value.trim().length > 0)
        .join(', ')
    },
    newBillingRoute() {
      return {
        name: 'new-billing',
        params: this.resolvedCustomerId ? { customerId: this.resolvedCustomerId } : {},
      }
    },
  },
  watch: {
    resolvedCustomerId: {
      immediate: true,
      handler() {
        this.loadTaxInformations()
      },
    },
  },
  methods: {
    ...mapActions('billing', ['fetchTaxInformations']),
    async loadTaxInformations() {
      if (!this.resolvedCustomerId) {
        this.taxInformations = []
        if (this.value) this.$emit('input', null)
        return
      }

      this.loading = true
      try {
        const response = await this.fetchTaxInformations({
          customerId: this.resolvedCustomerId,
          pagination: { page: 1, per_page: 200, all: true },
        })
        this.taxInformations = response?.data || []

        if (this.value && !this.taxInformations.some(item => item.id === this.value)) {
          this.$emit('input', null)
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.project-tax-information {
  margin-top: 1rem;
}

.gap-1 {
  gap: 0.5rem;
}

.project-tax-information__summary {
  border: 1px solid #e5e7eb;
  box-shadow: none;
}
</style>
